/* eslint-disable new-cap */
import React, { useRef, useState, useContext, useCallback } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { ArrowCircleLeft } from "phosphor-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import LogoImg from "assets/logo/DFMLogo.jpg";
import useOnClickOutside from "hooks/useOnClickOutside";
import { MapContext, MapActions } from "context/map";
import ExportMap from "components/Modal/ExportMapModal";
import CustomModal from "components/Modal";
import Button from "components/Button";
import FieldListMenu from "../FieldListMenu";
import LayerListMenu from "../LayerListMenu";
import AlgorithmListMenu from "../AlgorithmListMenu";
import ActionListMenu from "../ActionListMenu";
import "./index.scss";

const FarmFilter = ({ lastSelectedSourceRef }) => {
  const history = useHistory();
  const containerRef = useRef();
  const [menuOpened, setMenuOpened] = useState(null);
  const [isExport, setIsExport] = useState(false);
  const [isExceeded, setExceeded] = useState();
  const { state, dispatch } = useContext(MapContext);
  const { farm, exportMode, selectedDate, selectedLayer, selectedAlgorithm } =
    state;

  useOnClickOutside(containerRef, () => setMenuOpened(null));

  const handleBookEm = () => {
    history.push("/rapid-em-booking");
  };

  const handleExportMap = useCallback(() => {
    dispatch({ type: MapActions.SET_EXPORT_MODE, value: true });
    setIsExport(true);
  }, [dispatch]);

  const getselectedLayer = useCallback(() => {
    if (selectedLayer === "standard") {
      return "SR";
    }
    if (selectedLayer === "high_res") {
      return "HR";
    }
    if (selectedLayer === "em_sensor") {
      return "RAPID_EM";
    }
    return null;
  }, [selectedLayer]);

  const handleExport = useCallback(() => {
    if (exportMode) {
      // const input = map.getCanvas();
      const newInput = document.getElementById("map-wrapper");
      html2canvas(newInput).then((canvas) => {
        let pdf;
        if (canvas.width < canvas.height) {
          pdf = new jsPDF("p", "pt", "a4", true);
        }
        if (canvas.width > canvas.height) {
          pdf = new jsPDF("l", "pt", "a4", true);
        }
        if (canvas.width === canvas.height) {
          pdf = new jsPDF("p", "pt", "a4", true);
        }
        // pdf.setDrawColor(0);
        // pdf.setFillColor(0, 0, 0);
        // pdf.rect(
        //   0,
        //   0,
        //   pdf.internal.pageSize.width,
        //   pdf.internal.pageSize.height,
        //   "F"
        // );
        const bodyRows = () => {
          const imgData = canvas.toDataURL("img/png");
          const pdfWidth = pdf.internal.pageSize.width;
          const pdfHeight = pdf.internal.pageSize.height;

          const widthRatio = pdfWidth / canvas.width;
          const heightRatio = pdfHeight / canvas.height;
          const ratio = widthRatio > heightRatio ? heightRatio : widthRatio;

          const canvasWidth = canvas.width * ratio;
          const canvasHeight = canvas.height * ratio;
          const marginX = (pdfWidth - canvasWidth) / 2;
          let marginY = 0;
          if (canvas.width < canvas.height) {
            marginY = pdfHeight - canvasHeight;
          } else {
            marginY = (pdfHeight - canvasHeight) / 2;
          }

          pdf.addImage(
            imgData,
            "PNG",
            marginX,
            marginY,
            canvasWidth,
            canvasHeight,
            undefined,
            "FAST"
          );
        };

        pdf.autoTable({
          body: bodyRows(),
          didDrawPage(data) {
            // Header
            pdf.setFontSize(20);
            pdf.setTextColor(40);
            if (LogoImg) {
              pdf.addImage(
                LogoImg,
                "PNG",
                data.settings.margin.left - 40,
                0,
                110,
                40,
                undefined,
                "FAST"
              );
            }
            pdf.setTextColor(40);
            pdf.setFontSize(14);
            pdf.text(
              `${farm?.properties?.farm_name.toUpperCase()}`,
              pdf.internal.pageSize.width / 2,
              20,
              { align: "center" }
            );
            pdf.setFontSize(10);
            pdf.text(
              `${getselectedLayer()} - ${selectedAlgorithm?.toUpperCase()} / ${selectedDate}`,
              pdf.internal.pageSize.width / 2,
              30,
              { align: "center" }
            );
          },
          margin: { top: 30 },
        });
        pdf.save(`${farm?.properties?.farm_name}.pdf`);
      });
    }
    setIsExport(false);
    dispatch({ type: MapActions.SET_EXPORT_MODE, value: false });
  }, [
    dispatch,
    exportMode,
    farm,
    selectedDate,
    getselectedLayer,
    selectedAlgorithm,
  ]);

  const handleCancelExport = useCallback(() => {
    dispatch({ type: MapActions.SET_EXPORT_MODE, value: false });
    setIsExport(false);
  }, [dispatch]);

  const handleCreateZones = () => {
    window.location.replace(
      `${process.env.REACT_APP_DATA_FARMING_NEXT_GEN}/farms/${farm.id}/zoning`
    );
  };

  const handleStackAnalysis = () => {
    history.push(`/stack-analysis/${farm.id}`);
  };

  const renderFarmInformation = () => {
    return (
      <section className="farm-information">
        <button
          type="button"
          className="action-button"
          onKeyDown={() => history.push("/farms")}
          onClick={() => history.push("/farms")}
        >
          <ArrowCircleLeft size={20} color="#292b33" />
        </button>
        <p
          className="action-link"
          onKeyDown={() => history.push("/farms")}
          onClick={() => history.push("/farms")}
          aria-hidden
        >
          Go to Farms & Fields
        </p>
        <p className="farm-name">{farm.properties.farm_name}</p>
      </section>
    );
  };

  return (
    <div
      ref={containerRef}
      className="map-settings"
      style={exportMode ? { display: "none" } : {}}
    >
      {renderFarmInformation()}
      <FieldListMenu
        menuOpened={menuOpened}
        toggleMenu={setMenuOpened}
        lastSelectedSourceRef={lastSelectedSourceRef}
      />
      <LayerListMenu menuOpened={menuOpened} toggleMenu={setMenuOpened} />
      {!["zoning", "rice_harvest_prediction", "stack"].includes(
        selectedLayer
      ) && (
        <AlgorithmListMenu menuOpened={menuOpened} toggleMenu={setMenuOpened} />
      )}
      <div className="divider" />
      <ActionListMenu
        menuOpened={menuOpened}
        toggleMenu={setMenuOpened}
        handleBookEm={handleBookEm}
        handleExportMap={handleExportMap}
        handleCreateZones={handleCreateZones}
        handleStackAnalysis={handleStackAnalysis}
      />
      <ExportMap
        isOpen={isExport}
        handleOnClose={handleCancelExport}
        handleExport={handleExport}
        handleCancelExport={handleCancelExport}
      />
      <CustomModal
        isOpen={isExceeded}
        onRequestClose={() => setExceeded(false)}
        onClose={() => setExceeded(false)}
      >
        <div className="area-exceed">
          <h1 className="title">Zone area exceeded</h1>
          <p className="description">
            One or more of your fields exceeds the 800ha area limit for zoning.
            Please select smaller fields and try again or contact us at
            <a href="mailto:support@datafarming.com.au" className="link">
              support@datafarming.com.au
            </a>{" "}
            for assistance.
          </p>
          <Button
            color="white"
            label="Got it"
            type="outlined"
            onClick={() => setExceeded(false)}
          />
        </div>
      </CustomModal>
    </div>
  );
};

FarmFilter.defaultProps = {
  lastSelectedSourceRef: { current: null },
};

FarmFilter.propTypes = {
  lastSelectedSourceRef: PropTypes.shape({
    current: PropTypes.string,
  }),
};

export default FarmFilter;
